import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import type { SubmissionWithAssets, Asset } from '../types'
import { JOINT_DEFINITIONS, JOINT_ID_TO_ANATOMICAL, JOINT_ANATOMICAL_IDS, type JointId, type JointAnatomicalId } from '../data/joints'
import { getAdminToken } from './authToken'

// Hand position mapping for organized display
const PHOTO_POSITIONS = [
  { key: 'palm_down_spread', label: 'Palm Down - Spread', emoji: '🖐️' },
  { key: 'palm_down_fist', label: 'Palm Down - Fist', emoji: '👊' },
  { key: 'palm_up_spread', label: 'Palm Up - Spread', emoji: '✋' },
  { key: 'palm_up_fist', label: 'Palm Up - Fist', emoji: '👊' }
]

const VIDEO_POSITIONS = [
  { key: 'palm_down_movement', label: 'Palm Down Movement', emoji: '🎥' },
  { key: 'palm_up_movement', label: 'Palm Up Movement', emoji: '🎥' }
]

type ApiErrorBody = { error?: string; message?: string; details?: string }
type VideoDownloadBody = { status?: 'ready' | 'processing' | 'error'; url?: string; message?: string }
type SubmissionMeta = {
  age?: number | string
  height_unit?: 'ft' | 'cm'
  height_feet?: number | string
  height_inches?: number | string
  height_cm?: number | string
  weight_unit?: 'lb' | 'kg' | string
  weight_value?: number | string
  sex?: string
  gender?: string
  overall_pain?: number | string
  pain_score?: number | string
  left_hand_pain?: string[]
  right_hand_pain?: string[]
  left_hand_swelling?: string[]
  right_hand_swelling?: string[]
}

function renderImageLoadError(target: HTMLImageElement, url: string, modal = false) {
  target.style.display = 'none'
  if (!target.parentElement || target.parentElement.querySelector('[data-image-error="true"]')) return

  const errorDiv = document.createElement('div')
  errorDiv.setAttribute('data-image-error', 'true')
  errorDiv.className = modal
    ? 'w-full h-64 bg-red-100 rounded flex flex-col items-center justify-center text-red-600 p-4'
    : 'w-full h-full bg-red-100 rounded flex flex-col items-center justify-center text-red-600 text-sm p-2'

  const title = document.createElement('div')
  title.className = modal ? 'text-lg font-medium mb-2' : ''
  title.textContent = 'Image Load Error'

  const detail = document.createElement('div')
  detail.className = modal ? 'text-sm break-all' : 'text-xs break-all mt-1'
  detail.textContent = url

  errorDiv.appendChild(title)
  errorDiv.appendChild(detail)
  target.parentElement.appendChild(errorDiv)
}

// Function to convert wizard joint IDs to admin display IDs
function mapJointIds(wizardJointIds: string[]): string[] {
  return wizardJointIds
    .map((id) => {
      const mapped = JOINT_ID_TO_ANATOMICAL[id as JointId]
      if (mapped) return mapped
      return JOINT_ANATOMICAL_IDS.includes(id as JointAnatomicalId) ? id : null
    })
    .filter(Boolean) as string[]
}

// EXACT MATCH TO WIZARD: Joint coordinates copied from wizard (328px × 379px Figma dimensions)
// These coordinates are medically calibrated and match the wizard exactly
const JOINT_POSITIONS = {
  left: JOINT_DEFINITIONS.left.map((joint) => ({
    id: joint.anatomicalId,
    name: joint.label,
    x: joint.x,
    y: joint.y
  })),
  right: JOINT_DEFINITIONS.right.map((joint) => ({
    id: joint.anatomicalId,
    name: joint.label,
    x: joint.x,
    y: joint.y
  }))
}

// Guard against fallback/dev video UIDs that have no real CF Stream source
function isRealVideo(uid: string | null | undefined): boolean {
  return !!uid && !uid.startsWith('fallback-') && !uid.startsWith('dev-video-')
}

// Helper function to get assets by type and side
function getAssetsByType(assets: Asset[], type: 'photo' | 'video', side?: 'left' | 'right') {
  return assets.filter(asset => 
    asset.kind === type && 
    (side ? asset.side === side : true)
  )
}

// Helper function to get asset by position
function getAssetByPosition(assets: Asset[], type: 'photo' | 'video', side: 'left' | 'right', position: string) {
  return assets.find(asset => 
    asset.kind === type && 
    asset.side === side && 
    asset.position === position
  )
}

// Component for visual hand joint display - single condition type
function HandJointDisplay({ hand, selectedJoints, conditionType, title }: { 
  hand: 'left' | 'right', 
  selectedJoints: string[], 
  conditionType: 'pain' | 'swelling',
  title: string
}) {
  const joints = JOINT_POSITIONS[hand]
  
  return (
    <div className="bg-white rounded-lg p-4 border">
      <h4 className="font-semibold text-gray-800 mb-3 text-center">{title}</h4>
      {/* EXACT MATCH TO WIZARD: Container dimensions and aspect ratio */}
      <div className="relative w-full max-w-[320px] aspect-[328/379] mx-auto">
        {/* EXACT MATCH TO WIZARD: Hand background with same styling */}
        <div 
          className="absolute inset-0 bg-gray-50 rounded-lg"
          style={{ 
            backgroundImage: `url('/images/hand.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: hand === 'right' ? 'scaleX(-1)' : 'none'
          }}
        />
        {joints.map(joint => {
          const isSelected = selectedJoints.includes(joint.id)
          
          if (import.meta.env.DEV && isSelected) {
            console.log(`RENDERING JOINT: ${joint.id} (${joint.name}) at ${joint.x}%, ${joint.y}%`)
          }

          if (!isSelected) return null
          
          return (
            <div
              key={joint.id}
              className={`absolute w-5 h-5 rounded-full border-2 shadow-lg ${
                conditionType === 'pain' 
                  ? 'bg-red-500 border-red-700'
                  : 'bg-red-500 border-red-700'
              }`}
              style={{
                left: `${joint.x}%`,
                top: `${joint.y}%`,
                transform: 'translate(-50%, -50%)',
                zIndex: 10
              }}
              title={`${joint.name} (${conditionType})`}
            />
          )
        })}
      </div>
      <div className="mt-3 text-sm text-gray-600 text-center">
        {selectedJoints.length > 0 ? (
          <div className="flex items-center gap-2 justify-center">
            <div className={`w-3 h-3 rounded-full ${
              conditionType === 'pain' ? 'bg-red-500' : 'bg-red-500'
            }`}></div>
            <span>{selectedJoints.length} joint{selectedJoints.length !== 1 ? 's' : ''} affected</span>
          </div>
        ) : (
          <div className="text-gray-400">No joints affected</div>
        )}
      </div>
    </div>
  )
}

export default function AdminDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [data, setData] = useState<SubmissionWithAssets | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<{ asset: Asset, title: string } | null>(null)
  const [selectedPhoto, setSelectedPhoto] = useState<{ asset: Asset, title: string } | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [downloadingVideo, setDownloadingVideo] = useState(false)
  const [imagesHash, setImagesHash] = useState(import.meta.env.VITE_IMAGES_ACCOUNT_HASH || '')

  const handleDelete = async () => {
    if (!id) return
    try {
      const token = getAdminToken()
      const r = await fetch(`/api/admin/submissions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({} as ApiErrorBody)) as ApiErrorBody
        alert(`Error: ${err.error || err.message || 'Delete failed'}`)
        return
      }
      navigate('/admin')
    } catch (e) {
      alert(`Error: ${e instanceof Error ? e.message : 'Unknown'}`)
    }
  }

  const handleDownloadPhoto = async (asset: Asset, title: string) => {
    if (!asset.cf_image_id || !imagesHash) return
    try {
      const url = `https://imagedelivery.net/${imagesHash}/${asset.cf_image_id}/public`
      const response = await fetch(url)
      const blob = await response.blob()
      const blobUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `${title.replace(/\s+/g, '-')}.jpg`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'photo_download' } })
    }
  }

  const handleDownloadVideo = async (asset: Asset, title: string) => {
    if (!isRealVideo(asset.cf_stream_uid)) return
    setDownloadingVideo(true)
    try {
      const token = getAdminToken()
      const poll = async (): Promise<string | null> => {
        const r = await fetch(`/api/admin/video-download?uid=${asset.cf_stream_uid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (!r.ok) throw new Error(`Download API returned ${r.status}`)
        const data = await r.json() as VideoDownloadBody
        if (data.status === 'ready') return data.url || null
        if (data.status === 'processing') return null
        throw new Error(data.message || 'Download failed')
      }

      let url = await poll()
      if (!url) {
        // Poll every 2s, timeout after 30s
        const start = Date.now()
        while (!url && Date.now() - start < 30000) {
          await new Promise(r => setTimeout(r, 2000))
          url = await poll()
        }
      }
      if (url) {
        window.open(url, '_blank')
      } else {
        Sentry.captureMessage('Video download timed out', { tags: { action: 'video_download' } })
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'video_download' } })
    } finally {
      setDownloadingVideo(false)
    }
  }

  useEffect(() => {
    if (!id) return
    ;(async () => {
      try {
        const token = getAdminToken()
        const r = await fetch(`/api/submissions/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
        if (!r.ok) {
          throw new Error(`Failed to fetch submission: ${r.status}`)
        }
        const j: SubmissionWithAssets = await r.json()
        if (j.images_account_hash) setImagesHash(j.images_account_hash)
        setData(j)
      } catch (e) {
        Sentry.captureException(e, { tags: { action: 'load_submission' } })
        setError(e instanceof Error ? e.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  if (!id) return null

  if (loading) {
    return (
      <div className='min-h-screen p-6'>
        <div className='max-w-3xl mx-auto'>
          <p className='text-center'>Loading submission...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className='min-h-screen p-6'>
        <div className='max-w-3xl mx-auto space-y-4'>
          <Link to='/admin' className='text-blue-400 hover:underline'>← Back to Admin</Link>
          <div className='card p-4'>
            <h1 className='text-xl font-bold text-red-400'>Error Loading Submission</h1>
            <p className='text-neutral-300'>{error || 'Submission not found'}</p>
          </div>
        </div>
      </div>
    )
  }

  // Parse meta field properly with error handling
  let meta: SubmissionMeta = {}
  try {
    if (data.submission.meta) {
      if (typeof data.submission.meta === 'string') {
        meta = JSON.parse(data.submission.meta) as SubmissionMeta
      } else {
        meta = data.submission.meta as SubmissionMeta
      }
    }
  } catch (error) {
    Sentry.captureException(error, { tags: { action: 'parse_meta' } })
    meta = {}
  }
  
  // Extract hand identification
  const handId = data.submission.patient_unique_id || data.submission.study_id || 'Not Set'
  
  // Extract patient data with fallbacks - fix field names to match wizard
  const age = meta.age ? `${meta.age} years` : 'Not provided'
  
  // Handle height based on unit selection
  let height = 'Not provided'
  if (meta.height_unit === 'ft' && meta.height_feet && meta.height_inches) {
    height = `${meta.height_feet}'${meta.height_inches}"`
  } else if (meta.height_unit === 'cm' && meta.height_cm) {
    height = `${meta.height_cm} cm`
  }
  
  // Handle weight based on unit selection  
  let weight = 'Not provided'
  if (meta.weight_unit && meta.weight_value) {
    weight = `${meta.weight_value} ${meta.weight_unit}`
  }
  
  // Extract sex and gender
  const sex = meta.sex || 'Not provided'
  const gender = meta.gender || 'Not provided'
  
  const overallPainRaw = meta.overall_pain ?? meta.pain_score
  const overallPainValue = typeof overallPainRaw === 'number' ? overallPainRaw : Number(overallPainRaw)
  const overallPain = Number.isFinite(overallPainValue) ? overallPainValue : null
  
  // Extract joint data with safe array handling and ID mapping
  const leftPainJoints = mapJointIds(Array.isArray(meta.left_hand_pain) ? meta.left_hand_pain : [])
  const rightPainJoints = mapJointIds(Array.isArray(meta.right_hand_pain) ? meta.right_hand_pain : [])
  const leftSwellingJoints = mapJointIds(Array.isArray(meta.left_hand_swelling) ? meta.left_hand_swelling : [])
  const rightSwellingJoints = mapJointIds(Array.isArray(meta.right_hand_swelling) ? meta.right_hand_swelling : [])
  

  return (
    <div className='min-h-screen bg-gray-50 p-6'>
      {/* Header */}
      <div className='max-w-7xl mx-auto mb-6'>
        <div className='bg-white rounded-xl shadow p-6'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-4'>
              <Link 
                to='/admin' 
                className='text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2'
              >
                ← Back to Dashboard
              </Link>
              <div>
                <h1 className='text-2xl font-bold text-gray-900'>Hand Submission Review</h1>
                <div className='space-y-1'>
                  <p className='text-gray-900 font-semibold'>Study ID: {handId}</p>
                </div>
              </div>
            </div>
            <div className='flex items-center gap-3'>
              <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                data.submission.status === 'submitted' ? 'bg-green-100 text-green-800' :
                data.submission.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {data.submission.status.replace('_', ' ').toUpperCase()}
              </div>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className='px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4' onClick={() => setShowDeleteConfirm(false)}>
          <div className='bg-white rounded-xl shadow-xl max-w-sm w-full p-6' onClick={e => e.stopPropagation()}>
            <h3 className='text-lg font-semibold text-gray-900 mb-2'>Delete Submission</h3>
            <p className='text-gray-600 mb-4'>
              Are you sure you want to delete <strong>{data.submission.study_id || data.submission.id}</strong>? This will permanently remove the submission and all associated photos and videos.
            </p>
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className='px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200'
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className='px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='max-w-7xl mx-auto space-y-6'>
        {/* Patient Summary Row */}
        <div className='grid grid-cols-1 lg:grid-cols-4 gap-6'>
          {/* Demographics */}
          <div className='bg-white rounded-xl shadow p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Patient Demographics</h3>
            <div className='space-y-3'>
              <div>
                <span className='text-gray-600 text-sm'>Age:</span>
                <p className='font-semibold text-gray-900'>{age}</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>Sex:</span>
                <p className='font-semibold text-gray-900'>{sex}</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>Gender:</span>
                <p className='font-semibold text-gray-900'>{gender}</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>Height:</span>
                <p className='font-semibold text-gray-900'>{height}</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>Weight:</span>
                <p className='font-semibold text-gray-900'>{weight}</p>
              </div>
            </div>
          </div>

          {/* Overall Pain Assessment */}
          <div className='bg-white rounded-xl shadow p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Pain Assessment</h3>
            <div className='text-center'>
              <div className={`text-4xl font-bold mb-2 ${
                (overallPain ?? 0) >= 7 ? 'text-red-600' :
                (overallPain ?? 0) >= 4 ? 'text-yellow-600' :
                'text-green-600'
              }`}>
                {overallPain == null ? 'Not assessed' : `${overallPain}/10`}
              </div>
              <p className='text-gray-600 text-sm'>Overall Pain Level</p>
            </div>
          </div>

          {/* Submission Info */}
          <div className='bg-white rounded-xl shadow p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Submission Info</h3>
            <div className='space-y-3'>
              <div>
                <span className='text-gray-600 text-sm'>Progress:</span>
                <p className='font-semibold text-gray-900'>{data.submission.step} / 24 steps</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>Started:</span>
                <p className='font-semibold text-gray-900'>{new Date(data.submission.created_at).toLocaleString()}</p>
              </div>
              <div>
                <span className='text-gray-600 text-sm'>{(data.submission.status === 'submitted' || data.submission.step === 24) ? 'Completed:' : 'Last Updated:'}</span>
                <p className='font-semibold text-gray-900'>{new Date(data.submission.updated_at).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Media Summary */}
          <div className='bg-white rounded-xl shadow p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>Media Captured</h3>
            <div className='space-y-3'>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Photos:</span>
                <span className='font-semibold'>{getAssetsByType(data.assets, 'photo').length} / 8</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Videos:</span>
                <span className='font-semibold'>{getAssetsByType(data.assets, 'video').length} / 4</span>
              </div>
              <div className='flex justify-between'>
                <span className='text-gray-600'>Total:</span>
                <span className='font-semibold'>{data.assets.length} / 12</span>
              </div>
            </div>
          </div>
        </div>

        {/* Hand Joint Assessment - All 4 Individual Assessments (matching wizard steps 5-8) */}
        <div className='bg-white rounded-xl shadow p-6'>
          <h3 className='text-xl font-semibold text-gray-900 mb-6'>Joint Pain & Swelling Assessment</h3>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-6'>
            {/* Step 5: Left Hand Pain */}
            <HandJointDisplay 
              hand="left" 
              selectedJoints={leftPainJoints} 
              conditionType="pain"
              title="Left Hand Pain"
            />
            {/* Step 6: Right Hand Pain */}
            <HandJointDisplay 
              hand="right" 
              selectedJoints={rightPainJoints} 
              conditionType="pain"
              title="Right Hand Pain"
            />
            {/* Step 7: Left Hand Swelling */}
            <HandJointDisplay 
              hand="left" 
              selectedJoints={leftSwellingJoints} 
              conditionType="swelling"
              title="Left Hand Swelling"
            />
            {/* Step 8: Right Hand Swelling */}
            <HandJointDisplay 
              hand="right" 
              selectedJoints={rightSwellingJoints} 
              conditionType="swelling"
              title="Right Hand Swelling"
            />
          </div>
        </div>

        {/* Photo Grid - All 8 Individual Photos */}
        <div className='bg-white rounded-xl shadow p-6'>
          <h3 className='text-xl font-semibold text-gray-900 mb-6'>Hand Position Photos</h3>
          <p className='text-sm text-gray-600 mb-4'>Click any photo to view full size</p>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
            {['left', 'right'].map(side => 
              PHOTO_POSITIONS.map(position => {
                const asset = getAssetByPosition(data.assets, 'photo', side as 'left' | 'right', position.key)
                return (
                  <div key={`${side}-${position.key}`} className='border rounded-lg p-3'>
                    <div className='text-sm font-medium text-gray-700 mb-2 flex flex-col items-center gap-1'>
                      <div className='flex items-center gap-2'>
                        <span>{position.emoji}</span>
                        <span className='capitalize'>{side}</span>
                      </div>
                      <span className='text-xs text-gray-500 text-center'>{position.label}</span>
                    </div>
                    {asset?.cf_image_id && imagesHash ? (
                      <div className='w-full bg-gray-50 rounded overflow-hidden cursor-pointer hover:opacity-90 transition-opacity' style={{ aspectRatio: '3/4' }}>
                        <img
                          src={`https://imagedelivery.net/${imagesHash}/${asset.cf_image_id}/public`}
                          alt={`${side} hand ${position.label}`}
                          className='w-full h-full object-contain'
                          loading="lazy"
                          onClick={() => {
                            const title = `${side.charAt(0).toUpperCase() + side.slice(1)} Hand - ${position.label}`
                            setSelectedPhoto({ asset, title })
                          }}
                          onError={(e) => {
                            const url = e.currentTarget.src
                            console.error('Image failed to load:', url)
                            renderImageLoadError(e.currentTarget, url)
                          }}
                        />
                      </div>
                    ) : asset?.cf_image_id ? (
                      <div className='w-full bg-yellow-100 rounded flex flex-col items-center justify-center text-yellow-700 text-sm' style={{ aspectRatio: '3/4' }}>
                        <div>Config Missing</div>
                        <div className='text-xs'>VITE_IMAGES_ACCOUNT_HASH</div>
                        <div className='text-xs'>ID: {asset.cf_image_id}</div>
                      </div>
                    ) : (
                      <div className='w-full bg-gray-100 rounded flex items-center justify-center text-gray-500 text-sm' style={{ aspectRatio: '3/4' }}>
                        {asset ? 'No Image ID' : 'No Image Captured'}
                      </div>
                    )}
                  </div>
                )
              })
            ).flat()}
          </div>
        </div>

        {/* Video Grid - Compact Thumbnails */}
        <div className='bg-white rounded-xl shadow p-6'>
          <h3 className='text-xl font-semibold text-gray-900 mb-6'>Hand Movement Videos</h3>
          <p className='text-sm text-gray-600 mb-4'>Click any video thumbnail to view full size</p>
          <div className='grid grid-cols-2 lg:grid-cols-4 gap-4'>
            {['left', 'right'].map(side => 
              VIDEO_POSITIONS.map(position => {
                const asset = getAssetByPosition(data.assets, 'video', side as 'left' | 'right', position.key)
                const title = `${side.charAt(0).toUpperCase() + side.slice(1)} Hand - ${position.label}`
                if (import.meta.env.DEV) console.log(`Looking for video: ${side} ${position.key}, found:`, asset)
                return (
                  <div key={`${side}-${position.key}`} className='border rounded-lg p-3'>
                    <div className='text-sm font-medium text-gray-700 mb-2 flex flex-col items-center gap-1'>
                      <div className='flex items-center gap-2'>
                        <span>{position.emoji}</span>
                        <span className='capitalize'>{side}</span>
                      </div>
                      <span className='text-xs text-gray-500 text-center'>{position.label}</span>
                    </div>
                    {asset?.cf_stream_uid ? (
                      asset.cf_stream_uid.startsWith('fallback-') || asset.cf_stream_uid.startsWith('dev-video-') ? (
                        <div
                          className='w-full bg-yellow-50 rounded flex flex-col items-center justify-center text-yellow-700 text-sm cursor-pointer hover:bg-yellow-100 transition-colors'
                          style={{ aspectRatio: '3/4' }}
                          onClick={() => setSelectedVideo({ asset, title })}
                        >
                          <div className="text-2xl mb-2">📹</div>
                          <div className="text-center">
                            <div className="font-medium">Video Captured</div>
                            <div className="text-xs mt-1">{asset.cf_stream_uid.startsWith('fallback-') ? 'Upload failed — fallback stored' : 'Dev capture'}</div>
                          </div>
                          {import.meta.env.DEV && (
                            <div className="text-[10px] mt-2 text-gray-400 break-all px-2">{asset.cf_stream_uid}</div>
                          )}
                        </div>
                      ) : (
                        <div
                          className='w-full bg-black rounded overflow-hidden cursor-pointer hover:opacity-90 transition-opacity relative group'
                          style={{ aspectRatio: '3/4' }}
                          onClick={() => setSelectedVideo({ asset, title })}
                        >
                          <iframe
                            src={`https://iframe.videodelivery.net/${asset.cf_stream_uid}?muted=true&autoplay=false&controls=false&loop=true`}
                            className='w-full h-full pointer-events-none'
                            title={title}
                            onError={() => {
                              console.error('Video iframe failed to load:', asset.cf_stream_uid)
                            }}
                          />
                          <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30'>
                            <div className='bg-white bg-opacity-90 rounded-full p-3'>
                              <svg className='w-6 h-6' fill='currentColor' viewBox='0 0 24 24'>
                                <path d='M8 5v14l11-7z'/>
                              </svg>
                            </div>
                          </div>
                          {import.meta.env.DEV && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-[10px] text-green-400 p-1 truncate">{asset.cf_stream_uid}</div>
                          )}
                        </div>
                      )
                    ) : (
                      <div className='w-full bg-gray-100 rounded flex items-center justify-center text-gray-500 text-sm' style={{ aspectRatio: '3/4' }}>
                        {asset ? 'Video Error' : 'No Video'}
                      </div>
                    )}
                  </div>
                )
              })
            ).flat()}
          </div>
        </div>

        {/* Video Modal */}
        {selectedVideo && (
          <div className='fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4' onClick={() => setSelectedVideo(null)}>
            <div className='bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden' onClick={(e) => e.stopPropagation()}>
              <div className='p-4 border-b flex justify-between items-center'>
                <h4 className='font-semibold text-gray-900'>{selectedVideo.title}</h4>
                <div className='flex items-center gap-2'>
                  {isRealVideo(selectedVideo.asset.cf_stream_uid) && (
                    <button
                      onClick={() => handleDownloadVideo(selectedVideo.asset, selectedVideo.title)}
                      disabled={downloadingVideo}
                      className='text-gray-500 hover:text-blue-600 transition-colors p-1 disabled:opacity-50'
                      title='Download video'
                    >
                      {downloadingVideo ? (
                        <svg className='w-5 h-5 animate-spin' fill='none' viewBox='0 0 24 24'>
                          <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
                          <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z' />
                        </svg>
                      ) : (
                        <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                        </svg>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedVideo(null)}
                    className='text-gray-500 hover:text-gray-700 text-xl font-bold'
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className='p-4'>
                {selectedVideo.asset.cf_stream_uid?.startsWith('fallback-') || selectedVideo.asset.cf_stream_uid?.startsWith('dev-video-') ? (
                  <div className='w-full bg-yellow-50 rounded flex flex-col items-center justify-center text-yellow-700' style={{ aspectRatio: '3/4' }}>
                    <div className="text-4xl mb-4">📹</div>
                    <div className="text-center">
                      <div className="font-medium text-lg mb-2">Video Captured Successfully</div>
                      <div className="text-sm">{selectedVideo.asset.cf_stream_uid?.startsWith('fallback-') ? 'Upload to Stream failed — fallback stored' : 'Dev capture'}</div>
                      <div className="text-xs mt-2 text-gray-600">Raw video file available in system</div>
                      {import.meta.env.DEV && (
                        <div className="text-[10px] mt-3 text-gray-400 break-all">{selectedVideo.asset.cf_stream_uid}</div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className='w-full bg-black rounded overflow-hidden relative' style={{ aspectRatio: '3/4' }}>
                    <iframe
                      src={`https://iframe.videodelivery.net/${selectedVideo.asset.cf_stream_uid}?letterboxColor=transparent`}
                      allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                      allowFullScreen
                      className='w-full h-full'
                      title={selectedVideo.title}
                      onError={() => {
                        console.error('Video modal iframe failed:', selectedVideo.asset.cf_stream_uid)
                      }}
                    />
                    {import.meta.env.DEV && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-[10px] text-green-400 p-1 break-all">{selectedVideo.asset.cf_stream_uid}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Photo Modal - Optimized for Portrait Photos */}
        {selectedPhoto && (
          <div className='fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4' onClick={() => setSelectedPhoto(null)}>
            <div className='bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden' onClick={(e) => e.stopPropagation()}>
              <div className='p-4 border-b flex justify-between items-center'>
                <h4 className='font-semibold text-gray-900'>{selectedPhoto.title}</h4>
                <div className='flex items-center gap-2'>
                  {selectedPhoto.asset.cf_image_id && imagesHash && (
                    <button
                      onClick={() => handleDownloadPhoto(selectedPhoto.asset, selectedPhoto.title)}
                      className='text-gray-500 hover:text-blue-600 transition-colors p-1'
                      title='Download photo'
                    >
                      <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4' />
                      </svg>
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedPhoto(null)}
                    className='text-gray-500 hover:text-gray-700 text-xl font-bold'
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className='p-4 flex items-center justify-center bg-gray-50'>
                {selectedPhoto.asset.cf_image_id && imagesHash ? (
                  <img
                    src={`https://imagedelivery.net/${imagesHash}/${selectedPhoto.asset.cf_image_id}/public`}
                    alt={selectedPhoto.title}
                    className='max-w-full max-h-[70vh] object-contain rounded shadow-lg'
                    onError={(e) => {
                      const url = e.currentTarget.src
                      console.error('Image failed to load in modal:', url)
                      renderImageLoadError(e.currentTarget, url, true)
                    }}
                  />
                ) : selectedPhoto.asset.cf_image_id ? (
                  <div className='w-full h-64 bg-yellow-100 rounded flex flex-col items-center justify-center text-yellow-700'>
                    <div className="text-lg font-medium mb-2">Configuration Missing</div>
                    <div className="text-sm">VITE_IMAGES_ACCOUNT_HASH not set</div>
                    <div className="text-xs mt-2">Image ID: {selectedPhoto.asset.cf_image_id}</div>
                  </div>
                ) : (
                  <div className='w-full h-64 bg-gray-100 rounded flex items-center justify-center text-gray-500'>
                    <div className="text-lg">No Image Available</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}
